import { withTurnstile } from "@nexxt/account-ui";
import { unwrap, withSignal } from "@nexxt/api-client";
import type {
  appointmentSlotsDto,
  MeetingType,
  publicAppointmentDto,
  publicBookingDto,
} from "@nexxt/contracts/booking";
import { queryOptions, useMutation } from "@tanstack/react-query";
import type * as z from "zod";
import { api, publicApi, queryClient } from "../../api";

// The booking of a customer's appointments (ADR-237): the page of the personal link, on the public routes of
// gateway-frontrow, with the token in the body and never in a key of the cache, one link per page; and, for a signed-in
// user, the open campaigns and the start of one, which gives the page a link of the user's own.

export type PublicBooking = z.output<typeof publicBookingDto>;
export type PublicAppointment = z.output<typeof publicAppointmentDto>;
export type BookingSlots = z.output<typeof appointmentSlotsDto>;

const publicBookingKey = ["public-booking"] as const;

/** What the slots of a day are asked with: the token aside. */
export interface DayAsked {
  showroomLocation: string;
  date: string;
  brandOrgIds: string[];
  meetingType?: MeetingType | undefined;
}

export const bookingQueries = {
  /** The booking of the link: read once, a dead link is not tried again. */
  page: (token: string) =>
    queryOptions({
      queryKey: publicBookingKey,
      queryFn: ({ signal }) => unwrap(publicApi.public.booking.read.$post({ json: { token } }, withSignal(signal))),
      staleTime: Number.POSITIVE_INFINITY,
      retry: false,
      refetchOnWindowFocus: false,
    }),

  /** The slots of a showroom day for the brands chosen, as the domain counts them now. */
  slots: (token: string, day: DayAsked) =>
    queryOptions({
      queryKey: [...publicBookingKey, "slots", day],
      queryFn: ({ signal }) =>
        unwrap(publicApi.public.booking.slots.$post({ json: { token, ...day } }, withSignal(signal))),
      staleTime: 0,
    }),

  /** The campaigns the signed-in customer can book. */
  campaigns: () =>
    queryOptions({
      queryKey: ["booking", "campaigns"],
      queryFn: ({ signal }) => unwrap(api.booking.campaigns.$get({}, withSignal(signal))),
    }),
};

/**
 * The confirmation of the appointments, with the token of the page's Turnstile, one for all (ADR-312), and the box of
 * the consent to the measure of opens (ADR-314 §4): the appointments go into the page, the slots are read again.
 */
export function useBookAppointments(token: string) {
  return useMutation({
    mutationFn: ({
      turnstile,
      ...json
    }: {
      appointments: AppointmentInput[];
      openTracking?: OpenTracking;
      turnstile: string;
    }) =>
      unwrap(publicApi.public.booking.appointments.batch.$post({ json: { token, ...json } }, withTurnstile(turnstile))),
    onSuccess: ({ items }) =>
      queryClient.setQueryData(publicBookingKey, (page: PublicBooking | undefined) =>
        page ? { ...page, appointments: withAppointments(page.appointments, items) } : page,
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: [...publicBookingKey, "slots"] }),
  });
}

type BatchInput = Parameters<typeof publicApi.public.booking.appointments.batch.$post>[0]["json"];
type AppointmentInput = BatchInput["appointments"][number];
/** The box of the consent to the measure of opens (ADR-314 §4). */
type OpenTracking = NonNullable<BatchInput["openTracking"]>;

/** The appointments of the page with the ones just booked, in order of start. */
const withAppointments = (list: PublicAppointment[], booked: PublicAppointment[]) =>
  [...list.filter((a) => !booked.some((b) => b.id === a.id)), ...booked].sort((a, b) =>
    a.startsAt.localeCompare(b.startsAt),
  );

/**
 * A signed-in user opens the booking of a campaign: the page of the booking, with a link of the user's own. What the
 * page read with a link before goes, since its key does not name the link.
 */
export function useStartBooking() {
  return useMutation({
    mutationFn: (salesCampaignId: string) =>
      unwrap(api.booking.campaigns[":salesCampaignId"].start.$post({ param: { salesCampaignId } })),
    onSuccess: () => queryClient.removeQueries({ queryKey: publicBookingKey }),
  });
}
